import React, { useEffect, useState, useCallback } from 'react';
import { api, getAdmin } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ADMIN } from '@/constants/testIds';
import { toast } from 'sonner';
import { Plus, Trash2, Key } from 'lucide-react';

export default function AdminAdmins() {
  const me = getAdmin();
  const [admins, setAdmins] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [changePwOpen, setChangePwOpen] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/admin/admins'); setAdmins(data); }
    catch (e) { console.error('Failed to load admins:', e); toast.error('Failed to load admins'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.email}?`)) return;
    try { await api.delete(`/admin/admins/${a.id}`); toast.success('Removed'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid={ADMIN.adminsPage} className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase  text-muted-foreground">Access</p>
          <h1 className="font-extrabold text-3xl mt-1">Admins</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setChangePwOpen(true)} className="rounded-xl"><Key className="h-4 w-4 mr-1.5" />Change my password</Button>
          {me?.role === 'super-admin' && (
            <Button data-testid={ADMIN.adminNewButton} onClick={() => setCreateOpen(true)} className="rounded-xl bg-[#1E3A2F]"><Plus className="h-4 w-4 mr-1.5" />New admin</Button>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {admins.map((a) => (
          <Card key={a.id} className="rounded-xl p-4 border-border flex items-center justify-between gap-3">
            <div>
              <p className="font-medium">{a.name || a.email}</p>
              <p className="text-xs text-muted-foreground">{a.email}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={a.role === 'super-admin' ? 'border-[#1E3A2F]/40 text-[#1E3A2F]' : ''}>{a.role}</Badge>
              {me?.role === 'super-admin' && (
                <>
                  <Button variant="outline" size="sm" onClick={() => setResetTarget(a)} className="rounded-lg"><Key className="h-3.5 w-3.5 mr-1" />Reset password</Button>
                  {a.email !== me?.email && (
                    <Button variant="ghost" size="icon" onClick={() => remove(a)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
                  )}
                </>
              )}
            </div>
          </Card>
        ))}
      </div>

      {createOpen && <CreateAdminDialog onClose={() => setCreateOpen(false)} onDone={() => { setCreateOpen(false); load(); }} />}
      {resetTarget && <ResetPasswordDialog admin={resetTarget} onClose={() => setResetTarget(null)} />}
      {changePwOpen && <ChangePasswordDialog onClose={() => setChangePwOpen(false)} />}
    </div>
  );
}

function CreateAdminDialog({ onClose, onDone }) {
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'admin' });
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      await api.post('/admin/admins', form);
      toast.success('Admin created');
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-2xl bg-[hsl(var(--card))]">
        <DialogHeader><DialogTitle className="font-extrabold">New admin</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="grid gap-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div className="grid gap-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="grid gap-1.5"><Label>Temporary password (min 6 chars)</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
          <div className="grid gap-1.5"><Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="super-admin">Super-admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="pt-4">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={loading} className="rounded-xl bg-[#1E3A2F]">{loading ? 'Creating…' : 'Create'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ admin, onClose }) {
  const [pw, setPw] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      await api.post('/admin/reset-password', { email: admin.email, new_password: pw });
      toast.success('Password reset');
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-2xl bg-[hsl(var(--card))]">
        <DialogHeader><DialogTitle className="font-extrabold">Reset password</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Set a new password for {admin.email}. They should change it after signing in.</p>
        <Input className="mt-3" type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" />
        <DialogFooter className="pt-4">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={loading || pw.length < 6} className="rounded-xl bg-[#1E3A2F]">{loading ? 'Resetting…' : 'Reset password'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChangePasswordDialog({ onClose }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      await api.post('/admin/change-password', { current_password: current, new_password: next });
      toast.success('Password changed');
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-2xl bg-[hsl(var(--card))]">
        <DialogHeader><DialogTitle className="font-extrabold">Change password</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="grid gap-1.5"><Label>Current password</Label><Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} /></div>
          <div className="grid gap-1.5"><Label>New password (min 6 chars)</Label><Input type="password" value={next} onChange={(e) => setNext(e.target.value)} /></div>
        </div>
        <DialogFooter className="pt-4">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={loading || next.length < 6} className="rounded-xl bg-[#1E3A2F]">{loading ? 'Changing…' : 'Change password'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
